import React, { useEffect, useRef, useState } from 'react'
import styles from './ChatMessageList.module.scss'
import ChatMessage from '../ChatMessage/ChatMessage'
import { ReactComponent as Paper } from 'components/CRM/UI/icons/Paper.svg'
import ActionMenu from 'components/UI/ActionMenu/ActionMenu'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'
import asideModalStore from 'Stores/asideModalStore'
import EditNote from 'components/ContentBox/Global/GlobalModalSingle/templates/CRM/EditNote/EditNote'
import useLocalization from 'hooks/useLocalization'

const ChatMessageList = ({
  messages,
  setMessages,
  dealData,
  messageStatus,
  newMessageId,
  dealId,
  setDealData,
  fetchDealData,
  fetchMessages,
  pinPage,
  messageRefs,
  setPinPage,
  shouldScroll,
  setShouldScroll,
  searchTerm,
  funnelId,
  editTask,
  sessionId,
  channelId,
}) => {
  const containerRef = useRef(null)
  const noteRef = useRef(null)

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'customer_card',
    dict: {
      edit: 'Edit',
      delete: 'Delete',
      are_you_sure: 'Are you sure',
      confirm_note_deletion: 'Are you sure you want to delete the note',
      no: 'No',
      yes: 'Yes',
      edit_note: 'Edit Note',
      save: 'Save',
      today: 'Today',
      yesterday: 'Yesterday',
    },
  })

  const noteMenu = [
    { name: translate.edit, action: 'edit' },
    { name: translate.delete, action: 'delete' },
  ]

  useEffect(() => {
    if (!pinPage && shouldScroll) {
      scrollToBottom()
    }
  }, [messages, pinPage, shouldScroll])

  useEffect(() => {
    const hasPinnedMessages = messages.some((el) => el?.data?.is_pinned)
    if (!hasPinnedMessages) {
      setPinPage(false)
      setShouldScroll(true)
    }
  }, [messages])

  const scrollToBottom = () => {
    if (containerRef.current && noteRef.current) {
      containerRef.current.scrollTop =
        containerRef.current.scrollHeight - noteRef.current.offsetHeight
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }

    const lastMessageId = messages[messages.length - 1]?.data?.id
    if (lastMessageId && messageRefs.current[lastMessageId]) {
      messageRefs?.current[lastMessageId]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  const deleteNote = (data) => {
    swal({
      title: `${translate.are_you_sure}?`,
      text: `${translate.confirm_note_deletion}?`,
      icon: 'warning',
      buttons: [translate.no, translate.yes],
      dangerMode: true,
    }).then((yes) => {
      if (yes) {
        const apiConf = {
          method: 'put',
          link: `integration/deals/${dealId}`,
          crm: true,
        }

        const payload = {
          status_deal: dealData.status_deal?.id,
          deal_name: dealData.deal_name,
          manager: dealData.manager?.id,
          client: dealData.client?.id,
          budget: dealData.budget,
          city: dealData.city?.id,
          branch: dealData.branch?.id,
          program: dealData.program?.id,
          level: dealData.level?.id,
          reject_reason: dealData?.reject_reason,
          note: '',
          funnels: [funnelId],
        }
        superAxiosRequest(apiConf, payload)
          .then((res) => {
            setDealData((prevData) => ({
              ...prevData,
              note: res?.data?.note,
            }))
          })
          .catch((err) => {
            console.log(err)
          })
      }
    })
  }

  const editNote = () => {
    const ms = {
      modalBodyComponent: () => <EditNote />,
      button: translate.save,
      link: `integration/deals/${dealId}`,
      title: translate.edit_note,
      crm: true,
      is_put: true,
      isResponsive: true,
      onSubmitSuccess: (res) => {
        setDealData((prevData) => ({
          ...prevData,
          note: res?.note,
        }))
      },
      valid: {
        note: 'required',
      },
    }

    const md = {
      status_deal: dealData.status_deal?.id,
      deal_name: dealData?.deal_name,
      manager: dealData?.manager?.id,
      client: dealData?.client?.id,
      budget: dealData?.budget,
      city: dealData?.city?.id,
      branch: dealData?.branch?.id,
      program: dealData?.program?.id,
      level: dealData?.level?.id,
      funnels: [funnelId],
      note: dealData?.note.trim(),
    }

    asideModalStore.activity(ms, md)
  }

  const noteAction = (action) => {
    switch (action) {
      case 'edit':
        editNote()
        break
      case 'delete':
        deleteNote()
        break
      default:
        break
    }
  }

  const renderMessagesWithSeparators = () => {
    let currentDate = null

    return messages?.map((message, index) => {
      const messageDate = new Date(
        message?.data?.sent_at ||
          message?.data?.started_at ||
          message?.data?.created_at,
      )

      const showSeparator =
        !currentDate ||
        messageDate.toDateString() !== currentDate.toDateString()
      currentDate = messageDate

      return (
        <React.Fragment key={`${message.data?.id}-${index}`}>
          {showSeparator && (
            <div className={styles.separator}>
              <div />
              {formatDate(messageDate)}
              <div />
            </div>
          )}
          <ChatMessage
            key={`${message.data?.id}-${index}`}
            ref={(ref) => {
              if (message?.data?.id) {
                messageRefs.current[message.data.id] = ref
              }
            }}
            setShouldScroll={setShouldScroll}
            type={message.type}
            pinPage={pinPage}
            setMessages={setMessages}
            fetchMessages={fetchMessages}
            messages={messages}
            message={message.data}
            dealData={dealData}
            messageStatus={messageStatus}
            newMessageId={newMessageId}
            isHighlighted={message.data?.id === newMessageId}
            error={message.data?.sending_status === 3}
            searchTerm={searchTerm}
            funnelId={funnelId}
            editTask={editTask}
            dealId={dealId}
            sessionId={sessionId}
            channelId={channelId}
          />
        </React.Fragment>
      )
    })
  }

  const formatDate = (date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return translate.today
    } else if (date.toDateString() === yesterday.toDateString()) {
      return translate.yesterday
    } else {
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
      return date.toLocaleDateString(undefined, options)
    }
  }

  // Функция для парсинга разных типов ссылок
  const parseLinks = (text) => {
    if (!text) return text

    // Регулярное выражение для поиска URL, email и www ссылок
    const urlRegex =
      /((https?:\/\/|www\.)[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g

    // Находим все совпадения
    const matches = [...text.matchAll(urlRegex)]

    if (matches.length === 0) return text

    const result = []
    let lastIndex = 0

    matches.forEach((match, index) => {
      const fullMatch = match[0]
      const matchIndex = match.index

      // Добавляем текст до ссылки
      if (matchIndex > lastIndex) {
        result.push(text.substring(lastIndex, matchIndex))
      }

      // Определяем тип ссылки
      let href = fullMatch
      let target = '_blank'
      let rel = 'noopener noreferrer'
      let linkClass = styles.link

      if (fullMatch.startsWith('www.')) {
        href = `https://${fullMatch}`
      } else if (fullMatch.includes('@') && !fullMatch.startsWith('mailto:')) {
        href = `mailto:${fullMatch}`
        target = undefined
        rel = undefined
        linkClass = styles.emailLink
      }

      // Добавляем ссылку
      result.push(
        <a
          key={`link-${index}`}
          href={href}
          target={target}
          rel={rel}
          className={linkClass}
        >
          {fullMatch}
        </a>,
      )

      lastIndex = matchIndex + fullMatch.length
    })

    // Добавляем оставшийся текст
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex))
    }

    return result
  }

  return (
    <div className={styles.container}>
      <div className={styles.content} ref={containerRef}>
        {renderMessagesWithSeparators()}
      </div>
      {dealData?.note && (
        <div className={styles.noteBlock} ref={noteRef}>
          <Paper />
          <div className={styles.note}>
            <div> {parseLinks(dealData.note)}</div>
            <ActionMenu menu={noteMenu} action={noteAction} />
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatMessageList
